<template>
  <v-navigation-drawer
    class="tags-panel"
    v-lock-scroll="show"
    v-model="show"
    width="650px"
    temporary
    stateless
    right
    app
  >
    <unsaved-changes-alert
      v-if="userHasUnsavedChanges"
      @saveChanges="userHasUnsavedChanges = false"
      @closePanel="show = false"
    >
      <template #default>
        You haven't saved your changes. Exit anyway?
      </template>
      <template #exit>
        Exit
      </template>
      <template #save>
        Stay
      </template>
    </unsaved-changes-alert>

    <div class="tags-panel__header">
      <div class="tags-panel__title">
        {{ config.title }}
      </div>
      <div class="tags-panel__close" @click="handleClose">
        <icons-close color="#62646A" :width="17" :height="17" />
      </div>
    </div>

    <div class="tags-panel__content">
      <template v-if="!loading">
        <div v-if="!showAddInput" @click="showAddInput = true" class="tags-panel__add-button">
          <icons-add-tag />
          Add new tag
        </div>
        <div v-else class="tags-panel__input-wrapper tags-panel__input-wrapper--add">
          <input
            ref="addInput"
            v-model="newTagName"
            class="tags-panel__inline-input"
            placeholder="Add new tag"
            :maxlength="tagNameMaxLength"
            @keyup.enter="addTag"
            @keyup.esc="cancelAddTag"
          />
          <icons-close-circle
            @click.native="cancelAddTag"
            :height="24"
            :width="24"
            class="tags-panel__action-icon tags-panel__action-icon--cancel"
          />
          <icons-check-circle
            @click.native="addTag"
            :height="24"
            :width="24"
            :check-stroke-width="1.4"
            class="tags-panel__action-icon tags-panel__action-icon--confirm"
          />
        </div>

        <div class="tags-panel__table">
          <div class="tags-panel__table-header">
            <div class="tags-panel__table-header-cell tags-panel__col--tag">Tag</div>
            <div class="tags-panel__table-header-cell tags-panel__col--count">
              {{ config.columns[0].label }}
            </div>
            <div class="tags-panel__col--actions"></div>
          </div>

          <div class="tags-panel__table-body">
            <div
              v-for="tag in tags"
              :key="tag.id"
              :class="[
                'tags-panel__row',
                editingTagId === tag.id ? 'tags-panel__row--editing' : 'tags-panel__row--normal',
              ]"
            >
              <div class="tags-panel__cell tags-panel__col--tag">
                <div v-if="editingTagId === tag.id" class="tags-panel__input-wrapper">
                  <input
                    ref="editInput"
                    v-model="editingTagName"
                    class="tags-panel__inline-input"
                    :maxlength="tagNameMaxLength"
                    @keyup.enter="saveTagEdit(tag.id)"
                    @keyup.esc="cancelTagEdit"
                  />
                  <icons-close-circle
                    @click.native="cancelTagEdit"
                    :height="24"
                    :width="24"
                    class="tags-panel__action-icon tags-panel__action-icon--cancel"
                  />
                  <icons-check-circle
                    @click.native="saveTagEdit(tag.id)"
                    :height="24"
                    :width="24"
                    :check-stroke-width="1.4"
                    class="tags-panel__action-icon tags-panel__action-icon--confirm"
                  />
                </div>
                <span v-else>{{ tag.name }}</span>
              </div>

              <div class="tags-panel__cell tags-panel__col--count">
                {{ tag[config.columns[0].key] ?? 0 }}
              </div>

              <div class="tags-panel__cell tags-panel__col--actions">
                <template v-if="editingTagId !== tag.id">
                  <icons-edit
                    @click.native="editTag(tag)"
                    :width="17"
                    :height="12"
                    class="tags-panel__action-icon"
                  />
                  <icons-delete
                    @click.native="removeTag(tag.id)"
                    :width="14"
                    :height="17"
                    class="tags-panel__action-icon"
                  />
                </template>
              </div>
            </div>
          </div>
        </div>

        <div class="tags-panel__footer">
          <cg-button @click="saveChanges" class="tags-panel__save-button">
            Save changes
          </cg-button>
        </div>
      </template>

      <common-loader
        v-else
        flex
        relative
        minHeightAuto
        style="padding: 100px 0"
      />
    </div>
  </v-navigation-drawer>
</template>

<script>
import panelVModel from '@/mixins/panelVModel';
import { CgButton } from '@corporategiftcom/design-system';
import UnsavedChangesAlert from '@/components/myInventory/Panels/common/UnsavedChangesAlert.vue';

// ─── Configs ──────────────────────────────────────────────────────────────────

const CONFIGS = {
  users: {
    title: 'Manage tags',
    stateKey: 'users',
    fetchAction: 'users/getUserTags',
    syncAction: 'users/syncUserTags',
    columns: [{ label: 'Assigned Users', key: 'users_count' }],
  },
  inventory: {
    title: 'Manage inventory tags',
    stateKey: 'inventory',
    fetchAction: 'inventory/getInventoryTags',
    syncAction: 'inventory/syncInventoryTags',
    columns: [{ label: 'Assigned Items', key: 'items_count' }],
  },
};

// ─── Constants ────────────────────────────────────────────────────────────────

const TAG_NAME_MAX_LENGTH = 35;
const MAX_VISIBLE_ROWS = 12;
const ROW_HEIGHT_PX = 65;

// ─── Component ────────────────────────────────────────────────────────────────

export default {
  name: 'ManageTagsPanel',
  components: { UnsavedChangesAlert, CgButton },
  mixins: [panelVModel],
  props: {
    type: {
      type: String,
      default: 'users',
      validator: (val) => Object.keys(CONFIGS).includes(val),
    },
  },
  data() {
    return {
      loading: false,
      originalTags: [],
      showAddInput: false,
      newTagName: '',
      editingTagId: null,
      editingTagName: '',
      userHasUnsavedChanges: false,
      tagNameMaxLength: TAG_NAME_MAX_LENGTH,
    };
  },
  computed: {
    config() {
      return CONFIGS[this.type];
    },
    tags() {
      return this.$store.state[this.config.stateKey].tags ?? [];
    },
    tagNamesMap() {
      return new Map(this.tags.map((tag) => [tag.name.toLowerCase(), tag.id]));
    },
  },
  methods: {
    // ─── Close ────────────────────────────────────────────────────────────────

    handleClose() {
      const hasChanges =
        this.tags.length !== this.originalTags.length ||
        this.tags.some((tag, i) => {
          const original = this.originalTags[i];
          return !original || tag.id !== original.id || tag.name !== original.name;
        });

      if (hasChanges) {
        this.userHasUnsavedChanges = true;
        return;
      }

      this.show = false;
    },

    // ─── Reset ────────────────────────────────────────────────────────────────

    resetUiState() {
      this.showAddInput = false;
      this.newTagName = '';
      this.editingTagId = null;
      this.editingTagName = '';
      this.userHasUnsavedChanges = false;
    },

    // ─── Helpers ──────────────────────────────────────────────────────────────

    validateAndCheckTag(name, excludeId = null) {
      const trimmedName = name.trim().replace(/[<>]/g, '');

      if (!trimmedName.length || trimmedName.length > TAG_NAME_MAX_LENGTH) {
        this.$cgToast.error(`Tag name must be between 1 and ${TAG_NAME_MAX_LENGTH} characters`);
        return null;
      }

      const existingTagId = this.tagNamesMap.get(trimmedName.toLowerCase());
      if (existingTagId && existingTagId !== excludeId) {
        this.$cgToast.error('Tag with this name already exists');
        return null;
      }

      return trimmedName;
    },

    setTags(newTags) {
      this.$store.commit(`${this.config.stateKey}/setTags`, newTags);
    },

    // ─── Tag Actions ──────────────────────────────────────────────────────────

    addTag() {
      const validatedName = this.validateAndCheckTag(this.newTagName);
      if (!validatedName) return;

      this.setTags([
        ...this.tags,
        { id: `temp_${Date.now()}`, name: validatedName, users_count: 0, items_count: 0 },
      ]);
      this.newTagName = '';
      this.showAddInput = false;
    },

    cancelAddTag() {
      this.newTagName = '';
      this.showAddInput = false;
    },

    editTag(tag) {
      this.editingTagId = tag.id;
      this.editingTagName = tag.name;
      this.$nextTick(() => {
        const input = this.$refs.editInput?.[0] ?? this.$refs.editInput;
        input?.focus();
      });
    },

    saveTagEdit(tagId) {
      const validatedName = this.validateAndCheckTag(this.editingTagName, tagId);
      if (!validatedName) return;

      this.setTags(
        this.tags.map((tag) => (tag.id === tagId ? { ...tag, name: validatedName } : tag)),
      );
      this.cancelTagEdit();
    },

    cancelTagEdit() {
      this.editingTagId = null;
      this.editingTagName = '';
    },

    removeTag(tagId) {
      this.setTags(this.tags.filter((tag) => tag.id !== tagId));
    },

    // ─── Save ─────────────────────────────────────────────────────────────────

    saveChanges() {
      const tagsToSave = this.tags.map((tag) =>
        tag.isFromApi ? { id: tag.id, name: tag.name } : { name: tag.name },
      );

      this.loading = true;
      this.$store
        .dispatch(this.config.syncAction, tagsToSave)
        .then(() => {
          this.$store.dispatch(this.config.fetchAction).then(() => {
            this.originalTags = this.tags.map((tag) => ({ ...tag }));
          });
          this.userHasUnsavedChanges = false;
          this.$emit('saved');
          this.show = false;
          this.$cgToast.successBold('Successfully updated tags');
        })
        .catch((err) => {
          const errors = Object.values(err?.response?.data?.errors ?? {}).flat();
          const message = errors[0] ?? 'An error occurred, please contact our support';
          this.$cgToast.error(message);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  watch: {
    show(val) {
      if (!val) {
        this.loading = false;
        this.setTags(this.originalTags);
        this.resetUiState();
        return;
      }
      this.loading = true;
      this.$store
        .dispatch(this.config.fetchAction)
        .then(() => {
          this.originalTags = this.tags.map((tag) => ({ ...tag }));
          this.resetUiState();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    showAddInput(val) {
      if (!val) return;
      this.$nextTick(() => this.$refs.addInput?.focus());
    },
  },
};
</script>

<style lang="scss" scoped>
$row-height: 65px;
$max-visible-rows: 12;

.tags-panel {
  &::v-deep > .v-navigation-drawer__content {
    display: flex;
    flex-direction: column;
  }

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 12px;
    padding: 0 50px;
    margin-top: 60px;
  }

  &__title {
    color: #000;
    font-family: 'Lato-Light', sans-serif;
    font-size: 40px;
    line-height: 44px;

    & > b {
      font-family: 'Lato-Bold', sans-serif;
    }
  }

  &__close {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    width: 40px;
    height: 40px;
    min-width: 40px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 30px;
    padding: 0 50px;
    margin-top: 50px;
  }

  &__add-button {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #6e50ff;
    cursor: pointer;
    width: fit-content;
  }

  &__table {
    width: 100%;

    &-body {
      max-height: calc(#{$max-visible-rows} * #{$row-height});
      overflow-y: auto;
    }
  }

  &__col {
    &--tag {
      width: 200px;
      min-width: 200px;
    }

    &--count {
      width: 200px;
      min-width: 200px;
    }

    &--actions {
      flex: 1;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 20px;
    }
  }

  &__table-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 20px;
    border-bottom: 1px solid #d3d2d2;
    border-top: 1px solid #d3d2d2;
  }

  &__table-header-cell {
    font-weight: 700;
    font-size: 13px;
    line-height: 100%;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 20px;
    height: $row-height;
    transition: background-color 0.2s ease;

    &--normal {
      background-color: transparent;
      border-bottom: 1px solid #d3d2d2;

      &:hover {
        background-color: #f8f9fa;
      }
    }

    &--editing {
      background-color: #f6f5ff;
      border-bottom: 1px solid #d3d2d2;
    }
  }

  &__cell {
    display: flex;
    align-items: center;

    &.tags-panel__col--tag {
      span {
        font-weight: 400;
        font-size: 14px;
        line-height: 18px;
        letter-spacing: 0;
        overflow: hidden;
        word-break: break-word;

        @supports (display: -webkit-box) {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        @supports not (display: -webkit-box) {
          max-height: calc(18px * 2);
          display: block;
        }
      }
    }

    &.tags-panel__col--count {
      font-weight: 400;
      font-size: 14px;
      line-height: 18px;
      letter-spacing: 0;
    }
  }

  &__input-wrapper {
    display: flex;
    align-items: center;
    border: 1px solid #d3d2d2;
    border-radius: 4px;
    padding: 11px;
    gap: 8px;
    background: #fff;
    width: 100%;
    height: 40px;

    &--add {
      max-width: 250px;
    }

    &:focus-within {
      border-color: #6e50ff;
    }
  }

  &__inline-input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: #222325;
    background: transparent;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &::placeholder {
      color: #95979d;
    }
  }

  &__action-icon {
    cursor: pointer;
    flex-shrink: 0;

    &--cancel {
      &:hover ::v-deep circle { fill: #ff4d4d; stroke: #ff4d4d; }
      &:hover ::v-deep path { stroke: #fff; }
    }

    &--confirm {
      &:hover ::v-deep circle { fill: #6e50ff; stroke: #6e50ff; }
      &:hover ::v-deep line { stroke: #fff; }
    }
  }

  &__footer {
    text-align: left;
  }

  &__save-button {
    border-radius: 100px;
  }
}
</style>
